import { T, useIsDesktop, Bone } from '../reportTokens';
import DrillFrame, { MiniKPI } from './DrillFrame';
import SectionError from '../SectionError';
import SectionEmpty from '../SectionEmpty';
import { ApiTypeBadge, ApiStatusPill, parsePct, fmtPct, fmtMoney } from './offersData';
import { localizeCurrencyText, useCurrencyRevision } from '../../../utils/currency';
import type { OfferDiscountBreakdownResponse } from '../../../interface/offersReportInterface';

const SECTION_TITLE_STYLE: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: T.t2, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 10 };

export default function OfferDiscountBreakdownDrill({ data, loading, failure, onRetry, onOpenOffer }: {
    data: OfferDiscountBreakdownResponse | null;
    loading?: boolean;
    failure?: boolean;
    onRetry?: () => void;
    onOpenOffer: (id: string, name: string) => void;
}) {
    const isDesktop = useIsDesktop();
    // Re-render when the outlet's country resolves, so the currency symbol inside
    // backend text (type labels, offer names) is rewritten for non-US outlets.
    useCurrencyRevision();

    const totalDiscount = data?.totalDiscount ?? 0;
    const highestCostType = localizeCurrencyText(data?.highestCostType ?? null);
    const highestCostTypeAmount = data?.highestCostTypeAmount ?? 0;
    const avgPerRedemption = data?.avgDiscountPerRedemption ?? 0;

    // byType — per-type discount, ranked by amount, feeds both the progress bars
    // and the per-offer breakdown below.
    const typeRows = [...(data?.byType ?? [])].sort((a, b) => b.discountAmount - a.discountAmount);

    const isLoading = !!loading && !data;

    return (
        <DrillFrame isDesktop={isDesktop}>
            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 18, boxShadow: T.shadow }}>
                {/* ── Stamp KPIs — on failure/empty the cards stay put and show "—" ── */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                    <MiniKPI label="Total Discount" value={isLoading ? <Bone w={80} h={22} r={4} /> : data ? fmtMoney(totalDiscount) : '—'} />
                    <MiniKPI label="Highest Cost Type" value={isLoading ? <Bone w={100} h={22} r={4} /> : (data && highestCostType) ? <span>{highestCostType} <span style={{ fontSize: 12, color: T.t2, fontWeight: 500 }}>{fmtMoney(highestCostTypeAmount)}</span></span> : '—'} />
                    <MiniKPI label="Avg / Redemption" value={isLoading ? <Bone w={80} h={22} r={4} /> : data ? fmtMoney(avgPerRedemption) : '—'} />
                </div>

                {/* ── Per-type discount (progress bars) ──────────────────────── */}
                <div style={{ marginBottom: 18 }}>
                    {isLoading ? (
                        [0, 1, 2].map((i) => (
                            <div key={i} style={{ marginBottom: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <Bone w={70} h={16} r={4} />
                                    <Bone w={90} h={14} r={4} />
                                </div>
                                <Bone w={null} h={8} r={3} style={{ display: 'block' }} />
                            </div>
                        ))
                    ) : failure ? (
                        <SectionError kpiName="discount breakdown" onRetry={onRetry} size="medium" minHeight={140} />
                    ) : typeRows.length === 0 ? (
                        <SectionEmpty kpiName="discount breakdown" size="medium" minHeight={140} />
                    ) : (
                        typeRows.map((r) => {
                            // pct drives the bar width only; the printed share is the API's own value.
                            const pct = parsePct(r.percentage);
                            return (
                                <div key={r.type} style={{ marginBottom: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                        <ApiTypeBadge type={r.type} />
                                        <span style={{ fontSize: 13, color: T.t1, fontWeight: 600 }}>{fmtMoney(r.discountAmount)} <span style={{ color: T.t3, fontWeight: 400 }}>· {fmtPct(r.percentage)}</span></span>
                                    </div>
                                    <div style={{ background: T.hover, borderRadius: 3, height: 8 }}>
                                        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: T.accent, borderRadius: 3 }} />
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* ── Per-Offer Breakdown ────────────────────────────────────── */}
                <div>
                    <div style={SECTION_TITLE_STYLE}>Per-Offer Breakdown</div>
                    {isLoading ? (
                        [0, 1].map((g) => (
                            <div key={g} style={{ marginBottom: 14 }}>
                                <Bone w={120} h={16} r={4} style={{ marginBottom: 8, display: 'block' }} />
                                {[0, 1].map((i) => <Bone key={i} w={null} h={isDesktop ? 34 : 64} r={isDesktop ? 8 : 10} style={{ display: 'block', marginBottom: isDesktop ? 6 : 8 }} />)}
                            </div>
                        ))
                    ) : failure ? (
                        <SectionError kpiName="per-offer breakdown" onRetry={onRetry} size="small" minHeight={120} />
                    ) : typeRows.length === 0 ? (
                        <SectionEmpty kpiName="per-offer breakdown" size="small" minHeight={120} />
                    ) : (
                        typeRows.map((group) => (
                            <div key={group.type} style={{ marginBottom: 14 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                    <ApiTypeBadge type={group.type} />
                                    <span style={{ fontSize: 11, color: T.t3 }}>{group.offers.length} offer{group.offers.length === 1 ? '' : 's'}</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: isDesktop ? 0 : 8 }}>
                                    {[...group.offers].sort((a, b) => b.discountGiven - a.discountGiven).map((o) => (
                                        isDesktop ? (
                                            // Desktop — one line, but amount/uses/status sit in fixed-width
                                            // right-aligned slots so they form real columns across rows.
                                            <div key={o.offerId} onClick={() => onOpenOffer(o.offerId, o.offerName)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', borderBottom: `1px solid ${T.divider}` }}>
                                                <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: T.accent, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{localizeCurrencyText(o.offerName)}</span>
                                                <span style={{ width: 90, flexShrink: 0, fontSize: 12, color: T.t1, fontWeight: 600, textAlign: 'right' }}>{fmtMoney(o.discountGiven)}</span>
                                                <span style={{ width: 70, flexShrink: 0, fontSize: 11, color: T.t2, textAlign: 'right' }}>{o.redemptions} uses</span>
                                                <span style={{ width: 84, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}><ApiStatusPill status={o.status} /></span>
                                            </div>
                                        ) : (
                                            // Mobile — same card shape as the Offers table rows: name + status
                                            // on top, uses/discount pinned to the card edges underneath.
                                            <div key={o.offerId} onClick={() => onOpenOffer(o.offerId, o.offerName)} style={{ padding: 12, borderRadius: 10, border: `1px solid ${T.cardBorder}`, cursor: 'pointer' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                                                    <span style={{ flex: 1, minWidth: 0, marginRight: 8, fontSize: 13, color: T.accent, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{localizeCurrencyText(o.offerName)}</span>
                                                    <ApiStatusPill status={o.status} />
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span style={{ fontSize: 11, color: T.t3 }}>{o.redemptions} uses</span>
                                                    <span style={{ fontSize: 11, color: T.t1, fontWeight: 600 }}>{fmtMoney(o.discountGiven)}</span>
                                                </div>
                                            </div>
                                        )
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </DrillFrame>
    );
}
